'use client'

import { sortBy } from 'lodash'
import { useState } from 'react'

import ProviderImage from '@components/fad/provider-image'
import ProviderInsuranceModal from '@components/fad/provider-insurance-modal'
import { Icon, Link, Typography } from '@shc/ui'
import useAnalytics from '@hooks/use-analytics'
import type { TypeProvider } from '@lib/types'

const ProviderSpecialties = ({ provider }: { provider: TypeProvider }) => {
  return (
    <>
      {/* SPECIALTIES */}
      { provider.specialties && provider.specialties.length > 0 && (
        <div>
          <Typography variant="body-semibold">{provider.specialties.length > 1 ? 'Specialties' : 'Specialty'}</Typography>
          {sortBy(provider.specialties, ['specialty_name']).map(
            ({ specialty_id, specialty_name }) => {
              return (
                <div key={`spec-${specialty_id}`}>
                  <Typography className="inline">{specialty_name}</Typography>
                </div>
              )
            }
          )}
        </div>
      )}
    </>
  )
}

export type ProviderCardProps = {
  provider: TypeProvider
  site?: 'public' | 'providers'
}

const ProviderCard = ({ provider, site = "public" }: ProviderCardProps) => {
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false)
  const { track: trackSnowplow } = useAnalytics()

  // placing this into a var so that we can display it modularly/dynamically
  const providerInfo = (
    <div className="flex flex-col gap-x-4 gap-y-4">
      {/* PUBLIC SITE */}
      { site === 'public' && (
        <>
          <ProviderSpecialties provider={provider} />
          {/* BOARD CERTS */}
          { site === 'public' && provider.board_certifications && provider.board_certifications.length > 0 && (
            <div>
              <Typography variant="body-semibold">{provider.board_certifications.length > 1 ? 'Board certifications' : 'Board certification'}</Typography>
              {sortBy(provider.board_certifications, ['specialty_name']).map(
                ({ specialty_id, specialty_name }) => {
                  return (
                    <div key={`bc-${specialty_id}`}>
                      <Typography className="inline">{specialty_name}</Typography>
                    </div>
                  )
                }
              )}
            </div>
          )}
        </>
      )}
      {/* PROVIDER SITE */}
      { site === 'providers' && (
        <>
            <div>
            { provider.provider_types.map((pt: string) => {
                return (
                  <Typography key={pt} variant="body-semibold">{pt}</Typography>
                )}
              )}
            </div>
            { (provider.age_in_years || provider.gender ) && (
              <div>
                { provider.age_in_years && (
                  <>
                  <Typography as="span" variant="body-semibold">Age:</Typography>
                  <Typography className="inline ml-1">{provider.age_in_years}</Typography>
                  </>
                )}
                { provider.gender && (
                  <>
                    <Typography as="span" variant="body-semibold" className={provider.age_in_years ? 'ml-8' : ''}>Gender:</Typography>
                    <Typography className="inline ml-1">{provider.gender}</Typography>
                  </>
                )}
            </div>
            )}
          <div>
            { provider.options.includes('Accept new patients') ? (
              <Typography variant="body">
                <Icon className="w-3.5 pr-1.5 text-success" icon="check" />
                  Accepting new patients
              </Typography>
            ) : (
              <Typography variant="body">
                <Icon className="w-3.5 pr-1.5 text-danger" icon="xmark" />
                  Not accepting new patients
              </Typography>
            )}
          </div>
          <ProviderSpecialties provider={provider} />
        </>
      )}

      {/* INSURANCE*/}
      { site === 'public' && provider.insurances_facet && provider.insurances_facet?.length > 0 && (
        <div className="flex flex-row flex-nowrap gap-x-8">
          
          {/* INSURANCE */}
          { provider.insurances_facet &&
            provider.insurances_facet.length > 0 && (
              <div className="flex flex-col flex-wrap">
                <Typography variant="body-semibold" className="py-0.5">
                  Insurance
                </Typography>
                <Link
                  onClick={() => {
                    setIsInsuranceModalOpen(true)
                    trackSnowplow({
                      event: { name: 'modal_open', data: {} },
                      contexts: [{ name: 'modal', data: { modal_name: 'InsuranceModal' } }],
                    })
                  }}
                  noUnderline
                  aria-label="Accepted Insurance Plans">
                  Accepted plans
                </Link>
              </div>
            )}
        </div>
      )}
    </div>
  )

  return (
    <>
      {/* VIEW (auto, sm, md)  */}
      <div className="lg:hidden flex flex-col gap-y-4">
        <div className="flex flex-row gap-x-5">
          <ProviderImage provider={provider} className="min-w-fit" />
          <div className="flex flex-col gap-y-1 min-w-0">
            <Typography variant="h2" as="h1" className="break-words">
              {provider.name_formatted_with_degrees}
            </Typography>
          </div>
        </div>
        {providerInfo}
      </div>

      {/* VIEW (lg and up) */}
      <div className="hidden lg:flex flex-row gap-x-5 xl:gap-x-15">
        <ProviderImage provider={provider} site={site} className="min-w-fit" />
        <div className="flex flex-col gap-y-4 min-w-0">
          <div className="flex flex-col gap-y-1">
            <Typography variant="h2" as="h1" className="break-words">
              {provider.name_formatted_with_degrees}
            </Typography>
          </div>
          {providerInfo}
        </div>
      </div>

      {/* INSURANCE MODAL */}
      <ProviderInsuranceModal
        provider={provider}
        isModalOpen={isInsuranceModalOpen}
        setIsModalOpen={setIsInsuranceModalOpen}
      />
    </>
  )
}

export default ProviderCard
